import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';

const n = v => Number(v || 0);
const fmt = v => n(v).toLocaleString();

const LABELS = {
  INSURANCE_FL: { name:'Insurance agents — FL', cat:'Insurance' },
  INSURANCE_TX: { name:'Insurance agents — TX', cat:'Insurance' },
  DAC_TX:       { name:'Auto dealers — TX',     cat:'Auto dealers' },
  DAC_FL:       { name:'Auto dealers — FL',     cat:'Auto dealers' },
  DAYCARE_TX:   { name:'Daycare centers — TX',  cat:'Childcare' },
};
/* LICENSE_CHIPS_v1 */
const LIC = [
  { code:'PC',       key:'pc',       label:'P&C',           group:'P&C agents' },
  { code:'LH',       key:'lh',       label:'Life & health', group:'Life & health agents' },
  { code:'ADJUSTER', key:'adjuster', label:'Adjusters',     group:'Adjusters' },
  { code:'OTHER',    key:'other',    label:'Other',         group:'Other licenses' },
];
const licPrefix = s => String(s || '').replace('INSURANCE_', 'Insurance ');

const RAW = {
  'insurance.fl_licensee':  'Insurance licensees (FL DFS)',
  'carrier.carrier':        'Trucking carriers (FMCSA)',
  'realtor.license_holder': 'Realtors (TREC — emails redacted)',
  'daycare.state_licenses': 'Daycare licenses (TX HHSC)',
  'dac.mvsf_licenses':      'Dealer licenses (TxDMV)',
};

/* AGENCY_LABELS_v1 */
const CAT_ORDER = { 'Insurance':0, 'Auto dealers':1, 'Childcare':2, 'Other':3 };
const metaFor = (s) => {
  if (LABELS[s]) return LABELS[s];
  if (s === 'INSURANCE_AGENCY_ALL')   /* AGENCY_ALL_UI_v1 */
    return { name: 'All agency states \u2014 every licence class', cat: 'Insurance' };
  const m = /^INSURANCE_AGENCY_(.+)$/.exec(String(s || ''));
  if (m) return {
    name: m[1] === 'OTHER'
      ? 'Insurance agents \u2014 other states (agency)'
      : 'Insurance agents \u2014 ' + m[1] + ' (agency)',
    cat: 'Insurance'
  };
  return { name: s, cat: 'Other' };
};

const card = { border:'1px solid #eee', borderRadius:12, padding:14, background:'#fff' };
const chip = (on) => ({ fontSize:13, padding:'6px 12px', borderRadius:8, cursor:'pointer',
  border:'1px solid ' + (on?'#1a5cff':'#e2e2e2'), background:on?'#eef3ff':'#fff', color:on?'#1a5cff':'#555' });

export default function Databases() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [states, setStates] = useState([]);
  const [state, setState] = useState(null);
  const [cap, setCap] = useState(null);
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [name, setName] = useState('');
  const [excl, setExcl] = useState(true);
  const [licenseType, setLicenseType] = useState(null);   /* LICENSE_CHIPS_v1 */
  const [showAllSources, setShowAllSources] = useState(false);   /* SOURCE_SHOWMORE_v1 */
  const [exclShared, setExclShared] = useState(true);
  const [buckets, setBuckets] = useState(null);
  const [autoName, setAutoName] = useState('');
  const [overlaps, setOverlaps] = useState([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  const load = () => {
    client.get('/admin/marketing/databases')
      .then(r => setData(r.data)).catch(e => setErr('Could not load databases'))
      .finally(() => setLoading(false));
    client.get('/admin/marketing/databases/groups/overlaps')
      .then(r => setOverlaps(r.data || [])).catch(() => setOverlaps([]));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!source) { setStates([]); return; }
    setState(null);
    setLicenseType(null); setAutoName('');   /* LICENSE_CHIPS_v1 */
    client.get('/admin/marketing/databases/states?db=' + encodeURIComponent(source))
      .then(r => setStates(r.data || [])).catch(() => setStates([]));
  }, [source]);

  useEffect(() => {
    if (!source) { setCount(null); return; }
    setCounting(true);
    const q = '/admin/marketing/databases/preview?source=' + encodeURIComponent(source) +
              (state ? '&state=' + encodeURIComponent(state) : '') +
              (licenseType ? '&licenseType=' + encodeURIComponent(licenseType) : '') +
              (String(source).startsWith('INSURANCE_') && source !== 'INSURANCE_AGENCY_ALL' ? '&excludeShared=' + (exclShared ? 'true' : 'false') : '');   /* AGENCY_ALL_UI_v1 */
    client.get(q).then(r => setCount(r.data)).catch(() => setCount(null)).finally(() => setCounting(false));
  }, [source, state, licenseType, exclShared]);

  useEffect(() => {   /* LICENSE_CHIPS_v1 */
    if (!source || source === 'INSURANCE_AGENCY_ALL'   /* AGENCY_ALL_UI_v1 */
        || !String(source).startsWith('INSURANCE_')) { setBuckets(null); return; }
    client.get('/admin/marketing/databases/license-buckets?source=' + encodeURIComponent(source) +
               '&excludeShared=' + (exclShared ? 'true' : 'false'))
      .then(r => setBuckets(r.data || null)).catch(() => setBuckets(null));
  }, [source, exclShared]);

  const pickLicense = (l) => {   /* LICENSE_CHIPS_v1 */
    const next = (!l || licenseType === l.code) ? null : l.code;
    setLicenseType(next);
    if (!next) return;
    const sug = licPrefix(source) + ' - ' + LIC.find(x => x.code === next).group;
    if (!name.trim() || name === autoName) { setName(sug); setAutoName(sug); }
  };

  const save = async (thenSend) => {
    setErr(''); setMsg('');
    if (!name.trim()) return setErr('Give the group a name');
    try {
      const r = await client.post('/admin/marketing/databases/groups',
        { name: name.trim(), source, state, cap, excludeGrouped: excl,
          licenseType, excludeShared: exclShared });   /* LICENSE_CHIPS_v1 */
      setMsg('Saved "' + name.trim() + '" — ' + fmt(r.data.members) + ' contacts');
      setName(''); load();
      if (thenSend) navigate('/marketing?segment=' + r.data.segment);
    } catch (e) { setErr(e.response?.data || 'Save failed'); }
  };

  const del = async (g) => {
    if (!window.confirm('Delete group "' + g.name + '"?')) return;
    setErr(''); setMsg('');
    try { await client.delete('/admin/marketing/databases/groups/' + g.id); load(); }
    catch (e) { setErr(e.response?.data || 'Delete failed'); }
  };

  if (loading) return <div style={{ color:'#666', padding:20 }}>Loading...</div>;

  const rawBridged = (data?.bridged || []).filter(b => b.source !== 'admin-paste');   /* AGENCY_ALL_UI_v1 */
  const agencyTotal = rawBridged
    .filter(b => String(b.source).startsWith('INSURANCE_AGENCY_'))
    .reduce((t, b) => t + n(b.active), 0);
  const bridged = (agencyTotal > 0
      ? [{ source:'INSURANCE_AGENCY_ALL', active: agencyTotal }].concat(rawBridged)
      : rawBridged)   /* AGENCY_LABELS_v1 */
    .slice().sort((a, b) => {
      const ca = CAT_ORDER[metaFor(a.source).cat], cb = CAT_ORDER[metaFor(b.source).cat];
      return (ca === undefined ? 9 : ca) - (cb === undefined ? 9 : cb) || n(b.active) - n(a.active);
    });
  const visibleSources = showAllSources   /* SOURCE_SHOWMORE_v1 */
    ? bridged
    : bridged.slice(0, 10).concat(bridged.slice(10).filter(b => b.source === source));
  const eligible = count ? n(count.eligible) : 0;
  const grouped = count ? n(count.grouped) : 0;
  const pool = excl ? (count ? n(count.available) : 0) : eligible;
  const suppressed = count ? n(count.matched) - n(count.eligible) : 0;
  const capped = cap && cap < pool ? cap : pool;

  return (
    <div>
      <h1 style={{ fontSize:20, fontWeight:700, margin:'0 0 4px', color:'#111' }}>Databases</h1>
      <p style={{ color:'#666', margin:'0 0 18px', fontSize:14 }}>
        Filter a contact source into a saved group, then send it a campaign from Marketing.
      </p>

      {err && <div style={{ background:'#fff5f5', border:'1px solid #f0c0c0', borderRadius:8, padding:'8px 12px', color:'#a32d2d', fontSize:13, marginBottom:12 }}>{String(err)}</div>}
      {msg && <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:8, padding:'8px 12px', color:'#0a8a40', fontSize:13, marginBottom:12 }}>{msg}</div>}

      <div style={{ ...card, marginBottom:16 }}>
        <div style={{ fontSize:13, color:'#666', marginBottom:10 }}>Contact sources — pick one</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:8 }}>
          {visibleSources.map(b => {   /* SOURCE_SHOWMORE_v1 */
            const on = source === b.source;
            const meta = metaFor(b.source);   /* AGENCY_LABELS_v1 */
            return (
              <div key={b.source} onClick={() => setSource(on ? null : b.source)}
                style={{ border:'2px solid ' + (on?'#1a5cff':'#eee'), background:on?'#eef3ff':'#fff',
                  borderRadius:10, padding:12, cursor:'pointer', minWidth:0 }}>
                <div style={{ fontSize:11, color:'#999' }}>{meta.cat}</div>
                <div style={{ fontSize:13, color:on?'#1a5cff':'#111', marginTop:2 }}>{meta.name}</div>
                <div style={{ fontSize:20, fontWeight:700, color:on?'#1a5cff':'#111', marginTop:4 }}>{fmt(b.active)}</div>
              </div>
            );
          })}
        </div>
        {bridged.length > 10 && (   /* SOURCE_SHOWMORE_v1 */
          <div style={{ marginTop:10 }}>
            <span onClick={() => setShowAllSources(!showAllSources)}
              style={{ fontSize:13, color:'#1a5cff', cursor:'pointer' }}>
              {showAllSources
                ? 'Show fewer'
                : 'Show all ' + bridged.length + ' sources \u2193'}
            </span>
          </div>
        )}
      </div>

      {source && (
        <div style={{ ...card, marginBottom:16 }}>
          <div style={{ fontSize:13, color:'#666', marginBottom:10 }}>Filter</div>

          {buckets && String(source).startsWith('INSURANCE_') && (   /* LICENSE_CHIPS_v3 */
            <div style={{ marginBottom:12 }}>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                <span onClick={() => pickLicense(null)} style={chip(licenseType === null)}>All licenses</span>
                {LIC.map(l => (
                  <span key={l.code} onClick={() => pickLicense(l)} style={chip(licenseType === l.code)}>
                    {l.label} &middot; {fmt(buckets[l.key])}
                  </span>
                ))}
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'#666', cursor:'pointer' }}>
                <input type="checkbox" checked={exclShared} onChange={e => setExclShared(e.target.checked)} />
                Exclude shared inboxes{buckets.sharedInboxes ? ' (' + fmt(buckets.sharedInboxes) + ')' : ''}
              </label>
            </div>
          )}

          {states.length > 1 && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
              <span onClick={() => setState(null)} style={chip(state === null)}>All states</span>
              {states.map(s => (
                <span key={s.state} onClick={() => setState(s.state)} style={chip(state === s.state)}>
                  {s.state} &nbsp;{fmt(s.contacts)}
                </span>
              ))}
            </div>
          )}

          <div onClick={() => setExcl(!excl)} style={{ display:'flex', justifyContent:'space-between', gap:10, alignItems:'center',
            border:'1px solid ' + (excl?'#1a5cff':'#e2e2e2'), background:excl?'#eef3ff':'#fff',
            borderRadius:8, padding:'9px 12px', marginBottom:12, cursor:'pointer', fontSize:13, color:excl?'#1a5cff':'#666' }}>
            <span>{excl ? '\u2713 ' : ''}Exclude contacts already in another group</span>
            <span style={{ fontSize:11 }}>{excl ? 'on' : 'off'}</span>
          </div>

          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
            <span onClick={() => setCap(null)} style={chip(cap === null)}>All {fmt(pool)}</span>
            {[100, 500, 2000].map(v => (
              <span key={v} onClick={() => setCap(v)} style={chip(cap === v)}>{fmt(v)}</span>
            ))}
            <input type="number" min="1" value={cap || ''} placeholder="custom"
              onChange={e => setCap(e.target.value ? Math.max(1, parseInt(e.target.value,10)) : null)}
              style={{ width:110, padding:'6px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
          </div>

          <div style={{ background:'#f7f7f7', borderRadius:8, padding:'10px 12px', fontSize:13, marginBottom:12 }}>
            {counting ? 'Counting...' : count ? (
              <>
                <span style={{ color:'#0a8a40', fontWeight:600 }}>{fmt(capped)} will be saved</span>
                <span style={{ color:'#666' }}>
                  {' '}&middot; {fmt(count.matched)} match{state ? ' in ' + state : ''}
                  {grouped > 0 ? ' \u00b7 ' + fmt(grouped) + ' already grouped' : ''}
                  {suppressed > 0 ? ' \u00b7 ' + fmt(suppressed) + ' suppressed' : ''}
                  {' \u00b7 ' + fmt(pool) + ' available'}
                </span>
              </>
            ) : 'No count'}
          </div>

          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Group name"
              style={{ flex:1, minWidth:180, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
            <button onClick={() => save(false)} disabled={!capped}
              style={{ padding:'9px 16px', background:'#1a5cff', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600, opacity: capped?1:0.5 }}>
              Save as group
            </button>
            <button onClick={() => save(true)} disabled={!capped}
              style={{ padding:'9px 16px', background:'#fff', color:'#333', border:'1px solid #ddd', borderRadius:8, cursor:'pointer', fontSize:14, opacity: capped?1:0.5 }}>
              Save and send
            </button>
          </div>
        </div>
      )}

      <div style={{ ...card, marginBottom:16 }}>
        <div style={{ fontSize:13, color:'#666', marginBottom:10 }}>Saved groups</div>
        {overlaps.map(o => (
          <div key={o.group_a + '-' + o.group_b} style={{ background:'#fff8e6', border:'1px solid #f0d9a0', borderRadius:8,
            padding:'10px 12px', marginBottom:10, display:'flex', justifyContent:'space-between', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ fontSize:12, color:'#854f0b' }}>
              <strong>{o.name_a}</strong> and <strong>{o.name_b}</strong> share {fmt(o.shared)} contacts
              {n(o.shared) === n(o.count_a) ? ' \u2014 identical' : ''}
            </span>
            <button onClick={() => del({ id:o.group_b, name:o.name_b })}
              style={{ fontSize:12, border:'1px solid #ddd', borderRadius:8, padding:'5px 10px', cursor:'pointer', background:'#fff', color:'#a32d2d' }}>
              Delete {o.name_b}
            </button>
          </div>
        ))}
        {(data?.groups || []).length === 0 && (
          <div style={{ color:'#888', fontSize:14, padding:'12px 0' }}>No groups yet.</div>
        )}
        {(data?.groups || []).map(g => (
          <div key={g.id} style={{ border:'1px solid #eee', borderRadius:10, padding:12, marginBottom:8,
            display:'flex', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:14, color:'#111' }}>
                {g.name}
                <span style={{ fontSize:10, padding:'2px 8px', borderRadius:6, background:'#eef3ff', color:'#1a5cff', marginLeft:8 }}>GROUP_{g.id}</span>
              </div>
              <div style={{ fontSize:12, color:'#666', marginTop:2 }}>
                {(LABELS[g.base_source]||{}).name || g.base_source} &middot; {fmt(g.member_count)} saved &middot; {fmt(g.eligible)} still eligible
              </div>
              <div style={{ fontSize:11, color:'#999', marginTop:2 }}>
                {g.last_sent_at ? 'Last sent ' + new Date(g.last_sent_at).toLocaleDateString() : 'Never sent'}
              </div>
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'flex-start' }}>
              <button onClick={() => navigate('/marketing?segment=GROUP_' + g.id)}
                style={{ fontSize:12, border:'1px solid #ddd', borderRadius:8, padding:'6px 10px', cursor:'pointer', background:'#fff', color:'#1a5cff' }}>Send campaign</button>
              <button onClick={() => del(g)}
                style={{ fontSize:12, border:'1px solid #ddd', borderRadius:8, padding:'6px 10px', cursor:'pointer', background:'#fff', color:'#a32d2d' }}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={{ fontSize:13, color:'#666', marginBottom:10 }}>Raw source tables</div>
        {(data?.sources || []).map(s => {
          const key = s.schema_name + '.' + s.table_name;
          return (
            <div key={key} style={{ display:'flex', justifyContent:'space-between', gap:10,
              padding:'8px 0', borderBottom:'1px solid #f5f5f5', fontSize:13, flexWrap:'wrap' }}>
              <span style={{ color:'#333' }}>{RAW[key] || key}</span>
              <span style={{ color:'#666' }}>~{fmt(s.approx_rows)} rows</span>
            </div>
          );
        })}
        <div style={{ fontSize:11, color:'#999', marginTop:10 }}>Approximate counts from table statistics.</div>
      </div>
    </div>
  );
}
